import { Assistant } from '../types';
import { getPersonalContextEntries } from '../services/supabase';

export const generateSystemPrompt = async (assistant: Assistant): Promise<string> => {
  // Fetch fresh context from database instead of using potentially stale assistant.personal_context_entries
  const contextEntries = await getPersonalContextEntries(assistant.id);
  const contextContent = contextEntries.map(entry => `${entry.title}: ${entry.content}`).join('\n\n');

  return `You are ${assistant.name}'s Echoo (digital twin, or AI assistant) to help with answering questions about ${assistant.name}. 

CONTEXT ENTRIES:
${contextContent}

IMPORTANT INSTRUCTIONS:
- Always refer to ${assistant.name} in the third person perspective using ${assistant.pronouns ? ` (${assistant.pronouns})` : ''} pronouns (not "I" or "me")
- Only answer questions using the provided context above. Do not make up or invent any information
- If the provided context is not sufficient to answer a question, first try your best to answer the question based on the context entries without making things up, then clearly communicate that you don't have enough information rather than making things up
- Stay authentic to the original context as much as possible
- Include relevant information from all context entries when appropriate
- Keep responses concise but informative
- Make responses conversational and friendly`;
};

export const generateQuestionAnalysisPrompt = (questions: string[], assistantName: string): string => {
  return `Analyze these questions that people have asked ${assistantName} and provide a comprehensive analysis.

Questions to analyze:
${questions.map((q, i) => `${i + 1}. "${q}"`).join('\n')}

Please provide your analysis in the following JSON format:
{
  "categories": [
    {
      "category": "Category Name",
      "count": number of questions in this category,
      "examples": ["example question 1", "example question 2", "example question 3"]
    }
  ],
  "commonThemes": ["theme 1", "theme 2", "theme 3"],
  "insights": [
    "Insight 1 about what visitors are most interested in",
    "Insight 2 about patterns in the questions",
    "Insight 3 about potential areas to focus on"
  ]
}

Guidelines:
- Create 3-5 meaningful categories that group similar questions
- Identify 3-5 common themes that appear across multiple questions
- Provide 3-5 actionable insights about visitor interests and patterns
- Keep category names concise but descriptive
- Ensure examples are actual questions from the list
- Focus on business-relevant insights that help understand visitor needs

Return only the JSON object, no additional text.`;
};

export const generateContextCardsPrompt = async (assistant: Assistant): Promise<string> => {
  // Fetch fresh context from database instead of using potentially stale assistant.personal_context_entries
  const contextEntries = await getPersonalContextEntries(assistant.id);
  console.log('Fetched context entries for', assistant.name, ':', contextEntries);
  const contextContent = contextEntries.map(entry => `${entry.title}: ${entry.content}`).join('\n\n');

  return `Based on the following information about ${assistant.name}, generate 6 interesting topic categories that help people learn about different aspects of ${assistant.name} as a person. 

About ${assistant.name}:
${contextContent}

Please provide your response in the following JSON format:
{
  "topics": [
    {
      "id": "unique_id",
      "title": "Category Title",
      "emoji": "🎯",
      "prompt": "The specific question to ask about this category"
    }
  ]
}

Guidelines:
- Generate exactly 6 topic categories that would be interesting to visitors
- Create general category titles (2-4 words) that relate to ${assistant.name}'s background but are not overly specific
- Examples of good categories: "Recent projects" 🚀, "Work philosophy" 💭, "Career highlights" ⭐, "Personal interests" 🎨, "Industry insights" 🔍, "Collaboration style" 🤝
- If ${assistant.name} mentions specific projects, create a general category like "Recent projects" 🚀 rather than naming the specific project
- If ${assistant.name} has specific expertise, create categories like "Industry insights" 🔍 or "Professional advice" 💡
- Choose appropriate emojis that match the category theme and are visually appealing
- Make prompts a brief question that's strongly related to the category title, and would be answered by the context entries provided above, don't be too specific but try to be as relevant as possible, and use the words from the title in the prompt
- Focus on professional expertise, personal interests, and unique aspects that make ${assistant.name} interesting
- IMPORTANT: Base your categories and prompts on the actual content provided above. Do not create generic categories or prompts if the content doesn't support them
- If the content mentions specific skills, experiences, or interests, create categories that reflect those
- If the content is limited, create categories that help visitors learn more about ${assistant.name} in general

Return only the JSON object, no additional text.`;
};

export const generatePersonalContextCategoriesPrompt = (name: string, learningObjective: string): string => {
  return `Based on the following information, generate exactly 4 personal context categories that would help someone learn about ${name}.

Name: ${name}
Learning Objective: ${learningObjective}

Generate 4 categories that would inspire ${name} to fill out their personal context. Each category should have:
- A clear, engaging title
- A brief description of what this category covers
- A helpful prompt to guide what to write

Focus on categories that align with the learning objective. For example:
- If the objective is about professional expertise, include categories like "Professional Background", "Key Achievements", "Skills & Expertise"
- If the objective is about personal stories, include categories like "Personal Journey", "Life Experiences", "Values & Beliefs"
- If the objective is about creative work, include categories like "Creative Process", "Inspiration Sources", "Portfolio Highlights"

IMPORTANT: Return ONLY a valid JSON array with exactly 4 objects. Do not include any markdown formatting, code blocks, or additional text. Each object must have "title", "description", and "prompt" fields.

Example format:
[
  {
    "title": "Category Title",
    "description": "Category description",
    "prompt": "Category prompt"
  }
]`;
};
